import { Tooltip } from "@mui/material"
import { MoreHorizOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement, ReactNode } from "react"
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"

interface ChipData {
  id: string
  name: string
  maxWidth?: number
  icon?: ReactElement
}

interface LimitedSizeChipGroupProps {
  chips: ChipData[]
  maxSize: number
  renderChip: (chip: ChipData) => ReactNode // Function to render custom chip component
  renderMoreChip?: (hiddenChipsCount: number) => ReactNode // Optional function to render the "more" chip
}

const CHIP_GAP = 4 // Gap between chips
const MORE_ICON_WIDTH = 44 // Width of the "more" icon chip
const CHIP_PADDING = 16 // Combined padding for each chip

const LimitedSizeChipGroup: FC<LimitedSizeChipGroupProps> = ({
  chips,
  maxSize,
  renderChip,
  renderMoreChip, // Allow custom rendering for "more" chip
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState<number>(chips.length)
  const [hiddenChipsCount, setHiddenChipsCount] = useState<number>(0)
  const maxChipWidth = useMemo(() => maxSize - CHIP_GAP - MORE_ICON_WIDTH - CHIP_PADDING, [maxSize])

  const measureVisibleCount = useCallback(() => {
    if (!containerRef.current || !measureRef.current) return

    const containerWidth = containerRef.current.offsetWidth
    const chipElements = Array.from(measureRef.current.children)

    let totalWidth = 0
    let visibleChipCount = 0

    for (let i = 0; i < chipElements.length; i++) {
      const chipWidth = (chipElements[i] as HTMLElement).offsetWidth + CHIP_GAP

      if (totalWidth + chipWidth <= containerWidth || i === 0) {
        totalWidth += chipWidth
        visibleChipCount++
      } else {
        break
      }
    }

    setVisibleCount(visibleChipCount)
    setHiddenChipsCount(chips.length - visibleChipCount)
  }, [chips])

  useEffect(() => {
    measureVisibleCount()

    window.addEventListener("resize", measureVisibleCount)
    return () => window.removeEventListener("resize", measureVisibleCount)
  }, [measureVisibleCount])

  // Default function to render the "more" chip if no custom function is provided
  const defaultRenderMoreChip = (count: number) => (
    <Tooltip title={`${count} more`}>
      <div>{renderChip({ id: "more", name: "More", maxWidth: MORE_ICON_WIDTH, icon: <MoreHorizOutlined /> })}</div>
    </Tooltip>
  )

  return (
    <>
      <div className='invisible absolute' ref={measureRef}>
        {chips.map((hiddenChip) =>
          renderChip({ ...hiddenChip, id: `hidden-chip-${hiddenChip.id}`, maxWidth: maxChipWidth })
        )}
      </div>

      <div ref={containerRef} style={{ display: "flex", flexWrap: "nowrap", maxWidth: maxSize }}>
        {chips.slice(0, visibleCount).map((chip) => {
          return (
            <Tooltip key={"visible-chip" + chip.id} title={chip.name}>
              <div>{renderChip({ ...chip, maxWidth: maxChipWidth })}</div>
            </Tooltip>
          )
        })}
        {hiddenChipsCount > 0 &&
          (renderMoreChip ? renderMoreChip(hiddenChipsCount) : defaultRenderMoreChip(hiddenChipsCount))}
      </div>
    </>
  )
}

export default memo(LimitedSizeChipGroup)
